/**
 * MeetingSettings
 * Configure meeting bot behavior and notification preferences
 */

import React, { useState, useEffect } from 'react';
import { Settings, Save } from 'lucide-react';
import { MeetingSettings as MeetingSettingsType } from '../../../types';
import { Toggle } from '../../ui/Toggle';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import styles from './MeetingSettings.module.css';

export interface MeetingSettingsProps {
  settings: MeetingSettingsType;
  onSave: (settings: MeetingSettingsType) => void;
  isSaving?: boolean;
}

export const MeetingSettings: React.FC<MeetingSettingsProps> = ({
  settings: initialSettings,
  onSave,
  isSaving = false,
}) => {
  const [settings, setSettings] = useState<MeetingSettingsType>(initialSettings);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(initialSettings));
  }, [settings, initialSettings]);

  const handleToggle = (key: keyof MeetingSettingsType, value: boolean) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleNotificationToggle = (
    key: keyof MeetingSettingsType['notifications'],
    value: boolean
  ) => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: value,
      },
    });
  };

  const handleSave = () => {
    onSave(settings);
  };

  const handleReset = () => {
    setSettings(initialSettings);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Settings size={24} />
          <div>
            <h2 className={styles.title}>Meeting Bot Settings</h2>
            <p className={styles.subtitle}>
              Configure how the BrainIQ bot handles meetings and generates MoMs
            </p>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Bot Email Info */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Bot Configuration</h3>
          <div className={styles.infoBox}>
            <strong>Bot Email:</strong> brainiq-bot@yourdomain.com
            <p className={styles.infoText}>
              Invite this email address to your Microsoft Teams meetings to enable recording and
              MoM generation.
            </p>
          </div>
        </div>

        {/* Behavior Settings */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Bot Behavior</h3>
          <div className={styles.settingsList}>
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <strong className={styles.settingLabel}>Auto-Accept Invites</strong>
                <p className={styles.settingDescription}>
                  Automatically accept meeting invitations when the bot is invited
                </p>
              </div>
              <Toggle
                checked={settings.autoAccept}
                onChange={(checked) => handleToggle('autoAccept', checked)}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <strong className={styles.settingLabel}>Auto-Record</strong>
                <p className={styles.settingDescription}>
                  Start recording automatically when the bot joins the meeting
                </p>
              </div>
              <Toggle
                checked={settings.autoRecord}
                onChange={(checked) => handleToggle('autoRecord', checked)}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <strong className={styles.settingLabel}>Auto-Generate MoM</strong>
                <p className={styles.settingDescription}>
                  Generate Minutes of Meeting immediately after the meeting ends
                </p>
              </div>
              <Toggle
                checked={settings.autoGenerateMom}
                onChange={(checked) => handleToggle('autoGenerateMom', checked)}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <strong className={styles.settingLabel}>Auto-Send MoM</strong>
                <p className={styles.settingDescription}>
                  Automatically send MoM to meeting organizer after generation
                </p>
              </div>
              <Toggle
                checked={settings.autoSendMom}
                onChange={(checked) => handleToggle('autoSendMom', checked)}
              />
            </div>
          </div>
        </div>

        {/* JIRA Settings */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>JIRA Integration</h3>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Default JIRA Project</label>
            <select
              className={styles.select}
              value={settings.defaultJiraProject || ''}
              onChange={(e) =>
                setSettings({ ...settings, defaultJiraProject: e.target.value || undefined })
              }
            >
              <option value="">Select a project</option>
              <option value="DEMO">DEMO - Demo Project</option>
              <option value="PROJ">PROJ - Main Project</option>
              <option value="TEST">TEST - Test Project</option>
            </select>
            <p className={styles.fieldHint}>
              Default project for creating Epics and Stories from meeting action items
            </p>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Notification Preferences</h3>
          <p className={styles.sectionDescription}>
            Choose how you want to be notified about meeting updates
          </p>
          <div className={styles.checkboxList}>
            <Checkbox
              label="Email Notifications"
              checked={settings.notifications.email}
              onChange={(checked) => handleNotificationToggle('email', checked)}
            />
            <Checkbox
              label="Microsoft Teams Notifications"
              checked={settings.notifications.teams}
              onChange={(checked) => handleNotificationToggle('teams', checked)}
            />
            <Checkbox
              label="Slack Notifications"
              checked={settings.notifications.slack}
              onChange={(checked) => handleNotificationToggle('slack', checked)}
            />
            <Checkbox
              label="In-App Notifications"
              checked={settings.notifications.inApp}
              onChange={(checked) => handleNotificationToggle('inApp', checked)}
            />
          </div>
        </div>

        {/* MoM Template */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>MoM Template</h3>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Template Format</label>
            <select
              className={styles.select}
              value={settings.momTemplate}
              onChange={(e) => setSettings({ ...settings, momTemplate: e.target.value })}
            >
              <option value="standard">Standard - Comprehensive MoM</option>
              <option value="concise">Concise - Brief Summary</option>
              <option value="technical">Technical - Detailed Technical Notes</option>
              <option value="executive">Executive - High-Level Summary</option>
            </select>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className={styles.footer}>
        <Button variant="ghost" onClick={handleReset} disabled={!hasChanges || isSaving}>
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <Save size={16} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default MeetingSettings;
